import { useState, useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Link, useSearchParams } from "react-router-dom";
import EnvolvementThumbnail from "./EnvolvementThumbnail";
import EnvolvementThumbnailSkeleton from "./EnvolvementThumbnailSkeleton";
import { FiChevronLeft, FiChevronRight, FiSearch } from "react-icons/fi";

const backend_domain = process.env.REACT_APP_BACKEND_DOMAIN;
const backend_port = process.env.REACT_APP_BACKEND_PORT;

const validCategories = [
  "Gaming",
  "Interactive",
  "Livestreamed",
  "Video",
  "Other",
  "Art",
  "Tutorials",
  "Travel",
  "Music",
  "Q&A",
  "Education",
];

function ExplorePage() {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth0();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDataReady, setIsDataReady] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [viewAll, setViewAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams] = useSearchParams();
  const [showExpired, setShowExpired] = useState(false);

  useEffect(() => {
    const query = searchParams.get("q");
    if (query) {
      setSearchTerm(query);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isAuthLoading) return;
        const response = await fetch(
          `${backend_domain}${backend_port}/post/pull`
        );
        if (!response.ok) throw new Error("Network response was not ok");
        const postData = await response.json();
        setData(postData);
        setFilteredData(postData);
        setError(null);
        setTimeout(() => setIsDataReady(true), 300);
      } catch (error) {
        setError("Error loading Envolvments. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [isAuthLoading]);

  useEffect(() => {
    let filtered = data;

    if (selectedCategories.length > 0) {
      filtered = filtered.filter(
        (envolvement) =>
          envolvement.categories &&
          envolvement.categories.some((cat) => selectedCategories.includes(cat))
      );
    }

    if (!showExpired) {
      filtered = filtered.filter((item) => {
        return (
          (item.endDate?.none || new Date(item.endDate?.endDate) > new Date()) &&
          (!item.targetEnvolvmentDate ||
            new Date(item.targetEnvolvmentDate) > new Date())
        );
      });
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (envolvement) =>
          envolvement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          envolvement.description
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    setFilteredData(filtered);

    if (selectedCategories.length > 0 || searchTerm) {
      setViewAll(true);
    } else {
      setViewAll(false);
    }
  }, [selectedCategories, searchTerm, data, showExpired]);

  const handleCategoryFilter = (category) => {
    setSelectedCategories((prev) => {
      if (prev.includes(category)) {
        return prev.filter((cat) => cat !== category);
      } else {
        return [...prev, category];
      }
    });
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const clearFilters = () => {
    setSelectedCategories([]);
    setSearchTerm("");
  };

  const groupedByCategory = validCategories.reduce((acc, category) => {
    let items = data.filter(
      (envolvement) =>
        envolvement.categories && envolvement.categories.includes(category)
    );

    if (!showExpired) {
      items = items.filter((item) => {
        return (
          (item.endDate?.none || new Date(item.endDate?.endDate) > new Date()) &&
          (!item.targetEnvolvmentDate ||
            new Date(item.targetEnvolvmentDate) > new Date())
        );
      });
    }

    if (items.length > 0) acc[category] = items;
    return acc;
  }, {});

  const carouselRefs = useRef({});
  const scrollCarousel = (category, direction) => {
    const ref = carouselRefs.current[category];
    if (ref) {
      const scrollAmount = ref.offsetWidth;
      ref.scrollBy({
        left: direction === "right" ? scrollAmount : -scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const renderGroupedView = () => (
    <div className="space-y-16 py-10">
      {isLoading || !isDataReady
        ? validCategories.map((category) => (
          <div key={category} className="px-4 md:px-8">
            <h2 className="text-3xl font-bold mb-6">{category}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              <EnvolvementThumbnailSkeleton cards={4} />
            </div>
          </div>
        ))
        : Object.entries(groupedByCategory).map(([category, items]) => (
          <div key={category} className="relative">
            <div className="px-4 md:px-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold">{category}</h2>
                <div className="hidden md:flex items-center space-x-2">
                  <button
                    aria-label="Scroll left"
                    className="p-2 rounded-full bg-white dark:bg-gray-800 shadow-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => scrollCarousel(category, "left")}
                  >
                    <FiChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    aria-label="Scroll right"
                    className="p-2 rounded-full bg-white dark:bg-gray-800 shadow-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => scrollCarousel(category, "right")}
                  >
                    <FiChevronRight className="w-6 h-6" />
                  </button>
                </div>
              </div>
              <div
                ref={(el) => (carouselRefs.current[category] = el)}
                className="flex space-x-8 overflow-x-auto pb-4 scrollbar-hide"
              >
                {items.map((envolvement) => (
                  <div
                    key={envolvement._id}
                    className="w-64 sm:w-80 flex-shrink-0"
                  >
                    <EnvolvementThumbnail data={envolvement} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      <div className="flex justify-center mt-12">
        <button
          className="px-8 py-3 bg-primary-500 text-white rounded-full font-bold text-lg shadow-lg hover:bg-primary-600 transition-all duration-300 transform hover:scale-105"
          onClick={() => setViewAll(true)}
        >
          View All Envolvments
        </button>
      </div>
    </div>
  );

  const renderFullGridView = () => (
    <div className="px-4 md:px-8 py-10">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-bold">
          All Envolvments
          <span className="text-primary-500 ml-2">({filteredData.length})</span>
        </h2>
        <button
          className="px-6 py-2 bg-gray-200 text-gray-700 rounded-full font-bold shadow hover:bg-gray-300 transition-colors"
          onClick={clearFilters}
        >
          Back to Categories
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
        {!isDataReady || isAuthLoading || isLoading ? (
          <EnvolvementThumbnailSkeleton cards={12} />
        ) : filteredData.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <p className="text-xl text-gray-600 dark:text-gray-400">
              No Envolvments found.
            </p>
          </div>
        ) : (
          filteredData.map((data) => (
            <EnvolvementThumbnail key={data._id} data={data} />
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white min-h-screen">
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">
            Discover Your Next Envolvement
          </h1>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Browse through a variety of community-driven projects and find where
            you can make an impact.
          </p>
        </div>

        {/* Filters */}
        <div className="mb-12 space-y-6">
          <div className="relative max-w-lg mx-auto">
            <input
              type="text"
              placeholder="Search for envolvments..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full px-5 py-3 pr-12 rounded-full bg-white dark:bg-gray-800 shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <FiSearch className="absolute top-1/2 right-5 -translate-y-1/2 w-6 h-6 text-gray-400" />
          </div>
          <div className="flex overflow-x-auto py-2 space-x-3 justify-start md:flex-wrap md:justify-center md:space-x-0 md:gap-3 scrollbar-hide">
            {validCategories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryFilter(category)}
                className={`px-5 py-2 rounded-full text-base font-semibold transition-all duration-300 ease-in-out transform hover:scale-105 flex-shrink-0 ${selectedCategories.includes(category)
                  ? "bg-primary-500 text-white shadow-lg"
                  : "bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 shadow-md hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
              >
                {category}
              </button>
            ))}
            {(selectedCategories.length > 0 || searchTerm) && (
              <button
                onClick={clearFilters}
                className="px-5 py-2 rounded-full text-base font-semibold bg-red-100 text-red-700 hover:bg-red-200 transition-colors transform hover:scale-105 flex-shrink-0"
              >
                Clear Filters
              </button>
            )}
          </div>
          <div className="flex items-center justify-center">
            <label className="flex items-center space-x-2 cursor-pointer select-none px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={showExpired}
                  onChange={(e) => setShowExpired(e.target.checked)}
                  className="sr-only"
                />
                <div
                  className={`block w-10 h-6 rounded-full transition-colors ${showExpired ? "bg-primary-500" : "bg-gray-400 dark:bg-gray-600"
                    }`}
                ></div>
                <div
                  className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${showExpired ? "transform translate-x-4" : ""
                    }`}
                ></div>
              </div>
              <span className="text-gray-700 dark:text-gray-300 font-medium">
                Show Expired
              </span>
            </label>
          </div>
        </div>

        {viewAll ? renderFullGridView() : renderGroupedView()}

        {isAuthenticated && !error && isDataReady && (
          <div className="text-center py-12">
            <Link
              to={`/create`}
              className="px-8 py-4 bg-primary-500 text-white rounded-full font-bold text-xl shadow-lg hover:bg-primary-600 transition-all duration-300 transform hover:scale-105 inline-block"
            >
              Create Your Own Envolvement
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default ExplorePage;
